
import React, { useEffect, useState } from 'react';
import { getApiErrorMessage } from '@/lib/apiError';
import { resolveNotificationLink } from '@/lib/notificationsApi';
import { formatDateTime } from '@/lib/orgFormat';
import { useOrgFormat } from '@/lib/useOrgFormat';
import toast from 'react-hot-toast';
import { Link, useNavigate } from 'react-router-dom';
import { getCurrentTenant, getMyProfile, uploadTenantLogo, saveTenantLogo } from '@/lib/hrApi';
import { setCurrentTenant } from '@/features/auth/state/tenant-state/tenantSlice';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/app/hooks';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { updateUser } from '@/features/auth/state/auth-state/authSlice';
import { Avatar, AvatarFallback, AvatarImage } from '@/features/shared/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';
import { Bell, Building2, Gift, LogOut, User, Settings, HelpCircle, Globe, Check, CheckCheck, ChevronDown, Menu, ShieldCheck, Search, MessageSquare, Smile } from 'lucide-react';
import { useMessagesPanel } from '@/features/conversations/MessagesPanel';
import { StatusDialog } from '@/features/conversations/components/StatusDialog';
import { useEntitlements } from '@/features/entitlements/useEntitlements';
import { useSidebar } from '@/features/shared/components/ui/sidebar';
// import { ThemeToggle } from '@/components/theme/ThemeToggle'; // Component not available yet
import { LanguageSwitcher } from '@/features/shared/components/ui/language-switcher';
import { ThemeToggle } from '@/features/shared/theme/ThemeToggle';
import { useNotifications } from '@/features/hooks/useNotifications';

interface HeaderProps {
  userRole?: string;
  tenantSlug?: string;
  /** Opens the global command palette (⌘K). The header search box triggers this. */
  onOpenSearch?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ userRole: propUserRole, tenantSlug, onOpenSearch }) => {
  useOrgFormat(); // re-render notification timestamps when the org date format loads/changes
  const { t, i18n } = useTranslation(['header', 'common', 'conversations']);
  const { user } = useAppSelector((state) => state.auth);
  const { current: currentTenant } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { logout } = useLogout();
  const { items: notifications, unread, markRead, markAllRead } = useNotifications();
  const { isTenantAdmin, isSystemAdmin } = useRoleAccess();
  const { open: openMessages } = useMessagesPanel();
  const { hasProduct } = useEntitlements();
  const hasConversations = hasProduct('conversations');
  const { setOpenMobile } = useSidebar();
  const canEditBranding = isTenantAdmin || isSystemAdmin;
  const [uploadingLogo, setUploadingLogo] = useState(false);
  // "Set your status" — availability lives in conversation-service, so it's offered
  // only where the tenant holds that product.
  const [statusOpen, setStatusOpen] = useState(false);

  // THE "disappearing logo" fix: the header renders currentTenant.logo from the
  // Redux session state, which was only ever set by an in-session upload — a
  // fresh login/reload never rehydrated it from the saved tenant. Hydrate once
  // per session from the backend's logoUrl (also refreshes a renamed tenant).
  useEffect(() => {
    if (!currentTenant?.id || currentTenant.logo) return;
    let alive = true;
    getCurrentTenant()
      .then((tn) => {
        if (!alive || !tn?.logoUrl) return;
        dispatch(setCurrentTenant({ ...currentTenant, name: tn.name || currentTenant.name, logo: tn.logoUrl }));
      })
      .catch(() => undefined);
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.id, currentTenant?.logo]);

  const handlePickLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canEditBranding || !file.type.startsWith('image/')) return;
    setUploadingLogo(true);
    try {
      const url = await uploadTenantLogo(file);
      await saveTenantLogo(url);
      if (currentTenant) {
        dispatch(setCurrentTenant({ ...currentTenant, logo: `${url}?t=${Date.now()}` }));
      }
    } catch (err: any) {
      const detail = getApiErrorMessage(err);
      toast.error(getApiErrorMessage(err, detail ? `Logo upload failed: ${detail}` : 'Logo upload failed'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Pull the employee's profile into the auth user so the header avatar AND name
  // stay correct. Also self-heals a stale persisted name (e.g. after an admin
  // rename) without forcing a re-login — the canonical name comes from the profile.
  useEffect(() => {
    let active = true;
    getMyProfile()
      .then((me: any) => {
        if (!active || !me) return;
        const updates: Record<string, any> = {};
        if (!user?.avatar && me.profile_picture_url) updates.avatar = me.profile_picture_url;
        const fullName = [me.first_name, me.last_name].filter(Boolean).join(' ').trim();
        if (fullName && fullName !== user?.name) updates.name = fullName;
        if (Object.keys(updates).length) dispatch(updateUser(updates));
      })
      .catch(() => { /* not signed in / unavailable */ });
    return () => { active = false; };
  }, [user?.avatar, user?.name, dispatch]);

  const notificationsPath = tenantSlug ? `/t/${tenantSlug}/app/notifications` : '/app/notifications';
  // The bell's open state is controlled so opening a notification can CLOSE it.
  // The rows are plain buttons rather than DropdownMenuItem, so Radix never
  // sees a selection and never closes the menu on its own.
  const [bellOpen, setBellOpen] = useState(false);

  const handleOpenNotification = (n: { id: string; link?: string; isRead: boolean; entityType?: string; entityId?: string }) => {
    if (!n.isRead) markRead(n.id);
    // ⚠️ CLOSE THE MENU. Without this the navigation happens BEHIND the still-
    // open dropdown: the conversation really does load, the person is still
    // looking at the menu, and it reads as "I clicked it and nothing
    // happened". Reported exactly that way, and it took a screenshot to see
    // that the page underneath had changed all along.
    setBellOpen(false);
    navigate(resolveNotificationLink(n.link, tenantSlug, n) ?? notificationsPath);
  };


  const handleLogout = async () => {
    console.log('Logout clicked!');
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getInitials = (name?: string) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className="amashr-header h-16 border-b border-border px-3 md:px-6 flex items-center justify-between relative z-50 bg-card gap-2">
      <div className="flex items-center gap-2 md:gap-4 min-w-0">
        {/* Mobile: hamburger menu */}
        <button
          type="button"
          onClick={() => setOpenMobile(true)}
          className="md:hidden shrink-0 flex items-center justify-center w-9 h-9 rounded-lg text-charcoal hover:bg-muted transition-colors"
          aria-label="Open navigation menu"
        >
          <Menu className="w-6 h-6" />
        </button>

        {/* Tenant logo + name (all viewports). The platform brand mark lives in the
            side nav only — the top bar carries the tenant's own logo. */}
        <div className="flex items-center gap-2 min-w-0">
          <label
            className={`relative w-8 h-8 rounded-lg overflow-hidden flex items-center justify-center bg-orange-50 dark:bg-orange-500/[0.08] ${canEditBranding ? 'cursor-pointer group' : ''}`}
            title={canEditBranding ? 'Change company logo' : currentTenant?.name}
          >
            {currentTenant?.logo ? (
              <img src={currentTenant.logo} alt={currentTenant?.name || 'Logo'} className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-6 h-6 text-orange-500" />
            )}
            {canEditBranding && (
              <>
                <span className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[9px] font-medium">
                  {uploadingLogo ? '…' : 'Edit'}
                </span>
                <input type="file" accept="image/*" className="hidden" onChange={handlePickLogo} aria-label="Upload company logo" />
              </>
            )}
          </label>
          <div className="min-w-0">
            <h1 className="text-base md:text-xl font-bold text-charcoal leading-tight truncate">
              {currentTenant?.name || 'KaziPeople'}
            </h1>
            <p className="text-xs text-muted-foreground capitalize">
              {user?.role ? t(`common:${user.role}`, user.role.replace('-', ' ').replace('_', ' ')) : t('common:employee', 'Employee')}
            </p>
          </div>
        </div>
      </div>

      {/* Global search — opens the command palette (⌘K). Jump to any page, person,
          or action. Same style as before, but now it actually does something. */}
      <div className="flex-1 max-w-md mx-8 hidden md:block">
        <button
          type="button"
          onClick={onOpenSearch}
          className="w-full flex items-center gap-2 px-3.5 py-2 rounded-[10px] bg-cream border border-border hover:border-orange-400 transition-colors text-left"
        >
          <Search className="text-charcoal-light w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-[13px] text-muted-foreground">
            {t('header:search_placeholder', 'Search pages, people, actions…')}
          </span>
          <kbd className="text-[10px] font-medium text-muted-foreground border border-border rounded px-1.5 py-0.5 bg-white/60 dark:bg-white/10">
            ⌘K
          </kbd>
        </button>
      </div>

      <div className="flex items-center gap-2 md:gap-4 shrink-0">
        {/* Welcome Message */}
        <span className="text-sm text-muted-foreground hidden lg:block">
          {t('header:welcome', 'Welcome')}, <span className="text-charcoal font-bold">{(user?.name || user?.email || 'User').split(' ')[0]}</span> 👋
        </span>

        <span className="hidden sm:block"><LanguageSwitcher /></span>
        <ThemeToggle />

        {hasConversations && (
          <button
            type="button"
            aria-label="Messages"
            onClick={() => openMessages()}
            className="w-9 h-9 rounded-[10px] border border-border bg-cream text-charcoal-mid flex items-center justify-center hover:bg-muted hover:text-orange-500 transition-colors"
          >
            <MessageSquare className="w-[18px] h-[18px]" />
          </button>
        )}

        {/* ⚠️ Closing this menu used to call markAllRead().
            That is why notifications vanished: clicking one marked it read
            (correct), then navigating CLOSED the menu, which marked every
            other one read too — so coming back showed "You're all caught up".

            It was destructive beyond the bug, because the list renders only
            six (slice below) while markAllRead marks ALL: opening the bell
            once silently cleared notifications the person had never seen.

            Unread is somebody's record of what they have not dealt with.
            Only two things may clear it now: opening a notification, which
            marks that ONE, and the explicit "Mark all read" button. */}
        <DropdownMenu open={bellOpen} onOpenChange={setBellOpen}>
          <DropdownMenuTrigger asChild>
            <button type="button" aria-label="Notifications" className="relative w-9 h-9 rounded-[10px] border border-border bg-cream text-charcoal-mid flex items-center justify-center hover:bg-muted hover:text-orange-500 transition-colors">
              <Bell className="w-[18px] h-[18px]" />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 bg-orange-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-card">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="flex items-center justify-between px-2 py-1.5">
              <DropdownMenuLabel className="p-0">{t('notifications', { defaultValue: 'Notifications' })}</DropdownMenuLabel>
              {unread > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead()}
                  className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1"
                >
                  <CheckCheck className="w-3.5 h-3.5" /> {t('mark_all_read', { defaultValue: 'Mark all read' })}
                </button>
              )}
            </div>
            <DropdownMenuSeparator />
            {notifications.filter((n) => !n.isRead).length === 0 ? (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                {t('no_new_notifications', { defaultValue: "You're all caught up" })}
              </div>
            ) : (
              <div className="max-h-80 overflow-y-auto">
                {notifications.filter((n) => !n.isRead).slice(0, 6).map((n) => (
                  <button
                    key={n.id}
                    type="button"
                    onClick={() => handleOpenNotification(n)}
                    className={`w-full text-left px-3 py-2.5 hover:bg-muted/60 transition-colors flex gap-2 ${n.isRead ? '' : 'bg-orange-50/60 dark:bg-orange-500/10'}`}
                  >
                    <span className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${n.isRead ? 'bg-transparent' : 'bg-orange-500'}`} />
                    <span className="min-w-0">
                      {/* US-16: wrap to two lines instead of truncating a title mid-word. */}
                      <span className="block text-sm font-medium text-foreground line-clamp-2 break-words" title={n.title}>{n.title}</span>
                      {n.message && <span className="block text-xs text-muted-foreground line-clamp-2 break-words">{n.message}</span>}
                      <span className="block text-[11px] text-muted-foreground mt-0.5">{formatDateTime(n.createdAt)}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(notificationsPath)} className="justify-center text-sm text-orange-600">
              {t('view_all', { defaultValue: 'View all' })}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Account menu"
              className="flex items-center gap-2 py-1 pl-1 pr-2.5 rounded-full border border-border bg-cream hover:bg-muted transition-colors cursor-pointer"
            >
              <Avatar className="h-7 w-7">
                <AvatarImage src={user?.avatar || ''} alt={user?.name || 'User'} />
                <AvatarFallback className="bg-gradient-to-br from-orange-400 to-orange-600 text-white font-semibold text-xs">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs font-semibold text-charcoal hidden sm:block">{(user?.name || 'User').split(' ')[0]}</span>
              <ChevronDown className="w-3 h-3 text-charcoal-light" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.name || 'User'}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email || ''}
                </p>
                <p className="text-xs leading-none text-muted-foreground capitalize">
                  {user?.role ? t(`common:${user.role}`, user.role.replace('-', ' ').replace('_', ' ')) : t('common:employee', 'Employee')}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to={tenantSlug ? `/dashboard/${tenantSlug}/personal` : '/personal'}>
                <User className="mr-2 h-4 w-4" />
                <span>{t('header:profile', 'Profile')}</span>
              </Link>
            </DropdownMenuItem>
            {hasConversations && (
              <DropdownMenuItem onClick={() => setStatusOpen(true)} className="cursor-pointer">
                <Smile className="mr-2 h-4 w-4" />
                <span>{t('conversations:availability_menu', 'Set your status')}</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem asChild>
              <Link to={tenantSlug ? `/dashboard/${tenantSlug}/account` : '/account'}>
                <Settings className="mr-2 h-4 w-4" />
                <span>{t('header:settings', 'Settings')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={tenantSlug ? `/dashboard/${tenantSlug}/privacy` : '/privacy'}>
                <ShieldCheck className="mr-2 h-4 w-4" />
                <span>{t('header:privacy', 'Privacy & consent')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={tenantSlug ? `/dashboard/${tenantSlug}/refer` : '/refer'}>
                <Gift className="mr-2 h-4 w-4" />
                <span>{t('header:refer', 'Refer & Earn')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to={tenantSlug ? `/dashboard/${tenantSlug}/help` : '/help'}>
                <HelpCircle className="mr-2 h-4 w-4" />
                <span>{t('common:help_support', 'Help & Support')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            
            {/* Language Selector */}
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Globe className="mr-2 h-4 w-4" />
                <span>{t('header:language', 'Language')}</span>
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('en')}
                  className={i18n.language === 'en' ? 'bg-accent' : ''}
                >
                  <span className="mr-2">🇬🇧</span>
                  <span>English</span>
                  {i18n.language === 'en' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => i18n.changeLanguage('sw')}
                  className={i18n.language === 'sw' ? 'bg-accent' : ''}
                >
                  <span className="mr-2">🇹🇿</span>
                  <span>Kiswahili</span>
                  {i18n.language === 'sw' && (
                    <Check className="ml-auto h-4 w-4" />
                  )}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={handleLogout}
              className="text-red-600 focus:text-red-600 cursor-pointer"
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t('header:logout', 'Logout')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {hasConversations && <StatusDialog open={statusOpen} onOpenChange={setStatusOpen} />}
    </header>
  );
};
